/**
 * Componente PaymentMethod - Gerencia métodos de pagamento para agendamentos
 *
 * Funcionalidades implementadas:
 * - Aba "Particular": Permite definir valor da consulta e opção de cortesia
 * - Aba "Plano de saúde": Exibe chips com planos do paciente e permite seleção
 *
 * Planos do paciente:
 * - Chips verdes: Planos aceitos pela clínica (clicáveis)
 * - Chips cinzas: Planos não aceitos pela clínica
 * - Campo para número da carteirinha (preenchimento manual ou automático)
 * - Autocomplete para seleção do plano aceito
 * - Fluxo simplificado: clique no chip preenche automaticamente os campos
 */
import { endpoints } from "@/configs/Settings";
import { toast } from "react-toastify";
import type { CoverageFormData } from "@/pages/patientinsurance/components/CoverageModal";
import { useContractContext } from "@/contexts/ContractContext";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { useLocationContext } from "@/contexts/LocationContext";
import { GetAppointment } from "@/services/fhir";
import { useFhirData } from "@/utils/useFhirData";
import { useFormatter } from "@/utils/useFormatter";
import dayjs from "dayjs";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useWatch } from "react-hook-form";
import { ReturnAppointmentConfirmationDialog } from "./ReturnAppointmentConfirmationDialog";
import {
  Box,
  Stack,
  Tab,
  Tabs,
  Typography,
  Button,
  Skeleton,
} from "@mui/material";
import { AffiliationModal } from "@/pages/manageplans/components/AffiliationModal";
import { FormMultipleAutoComplete } from "@/components/form/FormMultipleAutoComplete";

import { FormSwitch } from "@/components/form/FormSwitch";
import { useOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/useOrganizationAffiliation";
import { useCoverage } from "@/services/fhir/coverage/useCoverage";
import { PostCoverage } from "@/services/fhir/coverage/PostCoverage";
import {
  FormAutoComplete,
  FormButtonGroup,
  FormTextInput,
} from "@/components/form";
import { OrganizationAffiliation } from "fhir/r5";
import { useAppointmentSubmission } from "./hooks/useAppointmentSubmission";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { Badge, Add, AttachMoney, Info, Warning } from "@mui/icons-material";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

interface IProps {
  setValue: any;
  setModalOpen: any;
  setTabRefetch?: Dispatch<SetStateAction<boolean>>;
  walkin: boolean;
  onBackButtonClick: Function;
  handleSubmit: Function;
  control: any;
}

export function PaymentMethod({
  setValue,
  setModalOpen,
  setTabRefetch,
  onBackButtonClick,
  handleSubmit,
  walkin,
  control,
}: IProps) {
  const { extractNumericValue } = useFormatter;
  const { organization, loading: loadingOrganization } =
    useOrganizationContext();

  const { contractHealthPlan } = useHealthPlanContractContext();
  const {
    organizationAffiliation,
    loading: loadingOrganizationAffiliation,
    fetchOrganizationAffiliation,
  } = useOrganizationAffiliation();

  const { location: locationContext } = useLocationContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const { currentContract } = useContractContext();

  // Hook centralizado para submissão de agendamentos
  const { submitAppointment, loading } = useAppointmentSubmission({
    setValue,
    setModalOpen,
    setTabRefetch,
    walkin,
    control,
  });
  const [isCheckingAppointment, setIsCheckingAppointment] =
    useState<boolean>(false);
  const [showReturnConfirmation, setShowReturnConfirmation] =
    useState<boolean>(false);
  const [showAffiliationModal, setShowAffiliationModal] =
    useState<boolean>(false);
  const [returnAppointmentDetails, setReturnAppointmentDetails] =
    useState<any>(null);

  const [appointmentIsReturn, setAppointmentIsReturn] =
    useState<any>(undefined);
  const [shouldShowServiceCost, setShouldShowServiceCost] =
    useState<boolean>(true);

  // Hook para buscar coberturas do paciente
  const { coverage, fetchCoverage, loading: loadingCoverage } = useCoverage();

  let patient = useWatch({ name: "patientActor", control });

  let healthcare = useWatch({ name: "healthCareServiceActor", control });

  let appointmentIsFree = useWatch({ name: "appointmentIsFree", control });

  let time = useWatch({ name: "slotInfo", control });
  let timeStart = useWatch({ name: "timeStart", control });
  let timeEnd = useWatch({ name: "timeEnd", control });
  let serviceCostWatch = useWatch({ name: "serviceCost", control });
  let originalServiceCost = useWatch({ name: "originalServiceCost", control });
  let healthInsuranceWatch = useWatch({
    name: "supportingInformation",
    control,
  });
  let cardNumber = useWatch({ name: "cardNumber", control });
  const scheduleCompletedWatch = useWatch({
    name: "scheduleCompleted",
    control,
  });

  const locationId = locationContext
    ?.map((element: fhir5.BundleEntry<fhir5.Location>) => element.resource?.id)
    ?.join(",");

  const hasSubscriptionFluxPay =
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: any) =>
        coding.system.includes("contract-type") &&
        coding.value === "fluxPayPlan"
    );

  // Função para lidar com a resposta do usuário ao modal de confirmação

  const [tabValue, setTabValue] = useState<number>(0);
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue("supportingInformation", undefined);
    setTabValue(newValue);
  };

  // Processa os procedimentos do plano selecionado
  const generateProcedureOptions = () => {
    if (!healthInsuranceWatch?.procedures) return [];

    return healthInsuranceWatch?.procedures?.map((spec: any) => ({
      label: spec.coding?.[0]?.display || "",
      value: spec.coding?.[0]?.code || "",
      price: spec.extension?.[0]?.valueMoney?.value || 0,
    }));
  };

  const organizationAffiliationOptions = useMemo(() => {
    return organizationAffiliation
      ?.filter((affiliation) => {
        return affiliation.active === undefined || affiliation.active === true;
      })
      ?.map((affiliation: OrganizationAffiliation) => {
        const insurancePlanExtension = affiliation.extension?.find(
          (ext) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
        );

        const defaultCardNumber = coverage?.find((option) => {
          return (
            insurancePlanExtension?.valueReference?.reference ===
            `InsurancePlan/${option.insurancePlan?.reference?.split("/")[1]}`
          );
        })?.identifier?.[0]?.value;

        return {
          value: affiliation.id || "",
          label: insurancePlanExtension?.valueReference?.display || "",
          procedures: affiliation.specialty,
          defaultCardNumber,
        };
      });
  }, [organizationAffiliation, coverage, patient]);

  const handleReturnConfirmation = (isReturn: boolean) => {
    setValue("appointmentIsFree", isReturn);
    setShouldShowServiceCost(!isReturn);
    setShowReturnConfirmation(false);
  };

  const searchAppointmentIsReturn = async () => {
    if (!patient?.reference || !healthcare?.reference || !hasFluxPayExtension)
      return null;

    setIsCheckingAppointment(true);
    try {
      const appointment = await GetAppointment({
        actor: locationId,
        patient: patient?.reference?.split("/")?.[1],
        healthcare: healthcare?.reference?.split("/")?.[1],
        count: 1,
        sort: "_lastUpdated",
        status: ["fulfilled"],
      });

      const returnAppointment = appointment?.entry?.[0]?.resource;
      setAppointmentIsReturn(returnAppointment);

      // Se encontrou um agendamento anterior, verifica se está dentro do período de retorno
      if (returnAppointment && hasFluxPayExtension) {
        // Verificação mais robusta para encontrar a extensão de dias de retorno
        let returnDaysExtension;

        // Verifica se scheduleCompletedWatch existe e tem extensões
        if (
          scheduleCompletedWatch?.extension &&
          Array.isArray(scheduleCompletedWatch.extension)
        ) {
          // Tenta encontrar a extensão pelo padrão "schedule-return-days"
          returnDaysExtension = scheduleCompletedWatch.extension.find(
            (ext: any) =>
              ext?.url &&
              typeof ext.url === "string" &&
              ext.url.includes("schedule-return-days")
          );

          // Se não encontrar, tenta outros padrões possíveis
          if (!returnDaysExtension) {
            returnDaysExtension = scheduleCompletedWatch.extension.find(
              (ext: any) =>
                ext?.url &&
                typeof ext.url === "string" &&
                (ext.url.includes("return-days") ||
                  ext.url.includes("returnDays") ||
                  ext.url.includes("return_days"))
            );
          }

          // Se ainda não encontrar, procura por qualquer extensão que tenha "days" no nome
          if (!returnDaysExtension) {
            returnDaysExtension = scheduleCompletedWatch.extension.find(
              (ext: any) =>
                ext?.url &&
                typeof ext.url === "string" &&
                ext.url.includes("days")
            );
          }

          // Verifica também se há alguma extensão com valueDuration ou valueInteger
          if (!returnDaysExtension) {
            returnDaysExtension = scheduleCompletedWatch.extension.find(
              (ext: any) => ext?.valueDuration?.value || ext?.valueInteger
            );
          }
        }

        // Obtém o número de dias para retorno (usa 30 como padrão se não encontrar)
        const returnDays =
          returnDaysExtension?.valueDuration?.value ||
          returnDaysExtension?.valueInteger ||
          30; // Valor padrão de 30 dias

        // Verifica se o agendamento anterior tem data de término
        if (returnAppointment?.end) {
          // Calcula a diferença em dias entre a data de término do agendamento anterior e hoje
          const endDate = dayjs(returnAppointment.end);
          const today = dayjs();
          const daysDifference = today.diff(endDate, "day");

          // Se a diferença for menor que o número de dias para retorno, pergunta ao usuário
          if (daysDifference < returnDays) {
            // Prepara os detalhes para exibir no modal
            const details = {
              ...returnAppointment,
              date: dayjs(returnAppointment.start).format("DD/MM/YYYY"),
              daysDifference,
              returnDays,
            };

            setReturnAppointmentDetails(details);

            // Abre o modal de confirmação com um pequeno atraso para garantir que o estado seja atualizado
            setTimeout(() => {
              setShowReturnConfirmation(true);
            }, 100);

            // Não define valores automaticamente, aguarda resposta do usuário
            return returnAppointment;
          } else {
            // Caso contrário, não é considerada consulta de retorno para fins de cortesia
            setShouldShowServiceCost(true);
          }
        } else {
          // Se não tiver data de término, usa a data de início
          const startDate = dayjs(returnAppointment.start);
          const today = dayjs();
          const daysDifference = today.diff(startDate, "day");

          if (daysDifference < returnDays) {
            // Prepara os detalhes para exibir no modal
            const details = {
              date: dayjs(returnAppointment.start).format("DD/MM/YYYY"),
              daysDifference,
              returnDays,
            };

            setReturnAppointmentDetails(details);

            // Abre o modal de confirmação com um pequeno atraso para garantir que o estado seja atualizado
            setTimeout(() => {
              setShowReturnConfirmation(true);
            }, 100);

            // Não define valores automaticamente, aguarda resposta do usuário
            return returnAppointment;
          } else {
            setShouldShowServiceCost(true);
          }
        }
      } else {
        // Se não encontrou agendamento anterior ou não tem FluxPay
        setShouldShowServiceCost(true);
      }

      return returnAppointment;
    } catch (error) {
      setShouldShowServiceCost(true);
      return null;
    } finally {
      setIsCheckingAppointment(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      // Se estiver na aba de plano de saúde e tiver um plano selecionado
      if (tabValue === 1 && data.supportingInformation) {
        // Encontrar a afiliação selecionada
        const selectedAffiliation = organizationAffiliation.find(
          (aff) => aff.id === data.supportingInformation.value
        );

        if (selectedAffiliation) {
          // Extrair informações do plano da extensão
          const insurancePlanExtension = selectedAffiliation.extension?.find(
            (ext) =>
              ext.url ===
              "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
          );

          const coverageData = {
            cardNumber: data.cardNumber,
            organization: {
              value:
                selectedAffiliation.participatingOrganization?.reference?.split(
                  "/"
                )[1] || "",
              label:
                selectedAffiliation.participatingOrganization?.display || "",
            },
            insurancePlan: {
              value:
                insurancePlanExtension?.valueReference?.reference?.split(
                  "/"
                )[1] || "",
              label: insurancePlanExtension?.valueReference?.display || "",
              display: insurancePlanExtension?.valueReference?.display,
            },
          };

          const references = {
            patientId: patient?.reference?.split("-")?.[1] || "",
          };

          try {
            const auxVerifyCoverage = healthInsuranceWatch?.defaultCardNumber;
            // Criar Coverage e atualizar lista
            if (!auxVerifyCoverage) {
              await PostCoverage(coverageData, references);
              await fetchCoverage({ patient: references.patientId });
            }

            // Atualiza a lista de coberturas para evitar duplicação
          } catch (error) {
            console.error("Erro ao criar Coverage:", error);
            toast.error("Erro ao vincular plano de saúde");
            // Não impede o agendamento se falhar ao criar Coverage
          }
        }
      }

      // Prepara os procedimentos selecionados para submissão
      const selectedProcedures = data.selectedProcedures || [];
      const selectedAffiliation = organizationAffiliation.find(
        (aff) => aff.id === data.supportingInformation?.value
      );

      // Adiciona os valores dos procedimentos baseado na afiliação
      const proceduresWithValues = selectedProcedures.map((proc: any) => {
        const specialty = selectedAffiliation?.specialty?.find(
          (spec) => spec.coding?.[0]?.code === proc.value
        );
        return {
          ...proc,
          price: specialty?.extension?.[0]?.valueMoney?.value || 0,
          currency: specialty?.extension?.[0]?.valueMoney?.currency || "BRL",
        };
      });

      // Continua com a submissão normal do agendamento
      await submitAppointment({
        ...data,
        appointmentIsFree,
        timeStart,
        timeEnd,
        slotInfo: time,
        procedures: proceduresWithValues,
      });
    } catch (error) {
      console.error("Erro ao processar agendamento:", error);

      // Se o Coverage foi criado mas o agendamento falhou, notifica o usuário

      toast.error("Erro ao processar agendamento");

      throw error;
    }
  };

  // Efeito separado para lidar com a verificação do tipo de consulta
  useEffect(() => {
    const checkAppointmentType = async () => {
      // Verifica agendamentos anteriores somente se tiver FluxPay
      if (hasFluxPayExtension) {
        await searchAppointmentIsReturn();
      } else {
        setShouldShowServiceCost(true);
      }
    };

    // Só executa se tiver paciente e serviço de saúde selecionados
    if (patient?.reference && healthcare?.reference) {
      checkAppointmentType();
    } else {
      setShouldShowServiceCost(true);
    }
  }, [patient, healthcare, scheduleCompletedWatch, hasFluxPayExtension]);

  // Buscar configurações de afiliação e coberturas do paciente
  useEffect(() => {
    const patientId = patient.reference.split("/")[1];
    if (organization?.id && patientId && tabValue === 1) {
      fetchOrganizationAffiliation({
        primaryOrganization: organization.id,
        count: 50,
      });
      fetchCoverage({ patient: patientId });
    }
  }, [
    organization?.id,
    fetchOrganizationAffiliation,
    loadingOrganization,
    tabValue,
  ]);

  const typeService = useWatch({ name: "typeService", control });

  if (
    (typeService === "FOLLOWUP" && showReturnConfirmation) ||
    !shouldShowServiceCost
  ) {
    return (
      <Stack spacing={2}>
        <Typography variant="body1" align="center">
          Esta é uma consulta de retorno. Clique em Finalizar para confirmar o
          agendamento.
        </Typography>
        <FormButtonGroup
          nextButtonText={"Finalizar"}
          onGoBackButtonClick={() => onBackButtonClick()}
          onNextButtonClick={handleSubmit(onSubmit)}
          loading={loading}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {contractHealthPlan && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="Particular" {...a11yProps(0)} />
            <Tab label="Plano de saúde" {...a11yProps(1)} />
          </Tabs>
        </Box>
      )}

      <CustomTabPanel value={tabValue} index={0}>
        {shouldShowServiceCost && hasFluxPayExtension && (
          <Stack
            mt={2}
            direction="row"
            spacing={2}
            justifyContent="space-between"
          >
            <FormTextInput
              control={control}
              label=""
              name="serviceCost"
              placeholder={serviceCostWatch ? "" : "R$ 0,00"}
              mask={useFormatter.formatCurrency}
              disabled={appointmentIsFree || isCheckingAppointment}
              validate={(value: string) => {
                if (
                  !hasSubscriptionFluxPay &&
                  (!value || value === "" || value === "R$ 0,00")
                ) {
                  return true;
                }
                const numericValue = Number(extractNumericValue(value));
                if (hasSubscriptionFluxPay) {
                  const originalValue = Number(
                    extractNumericValue(originalServiceCost)
                  );
                  if (numericValue < originalValue) {
                    return `O valor mínimo é ${originalServiceCost}`;
                  }
                } else if (numericValue < 5) {
                  return "O valor mínimo é R$ 5,00";
                }
                return true;
              }}
            />
            {!hasSubscriptionFluxPay && (
              <FormSwitch
                control={control}
                name="appointmentIsFree"
                label="Cortesia"
              />
            )}
          </Stack>
        )}
      </CustomTabPanel>

      <CustomTabPanel value={tabValue} index={1}>
        <Stack spacing={2}>
          {loadingOrganizationAffiliation || loadingCoverage ? (
            <Stack spacing={1}>
              <Skeleton variant="text" height={80} />
              <Skeleton variant="text" height={80} />
            </Stack>
          ) : organizationAffiliationOptions &&
            organizationAffiliationOptions.length > 0 ? (
            <FormAutoComplete
              name="supportingInformation"
              control={control}
              disabled={loadingOrganizationAffiliation || loadingCoverage}
              label="Plano de saúde"
              required={true}
              selectOptions={organizationAffiliationOptions}
              loading={loadingOrganizationAffiliation || loadingCoverage}
              renderOption={(option: any) => (
                <Stack
                  p={0.5}
                  direction="row"
                  gap={1}
                  alignItems="center"
                  width="100%"
                  justifyContent="space-between"
                >
                  <Typography variant="body1">{option.label}</Typography>
                  {option.defaultCardNumber && (
                    <Stack direction="row" alignItems="flex-end" gap={1}>
                      <Badge />
                      <Typography variant="body1">
                        {option.defaultCardNumber}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}
              auxOnChange={(option: any) => {
                if (option && option.defaultCardNumber) {
                  setValue("cardNumber", option.defaultCardNumber);
                } else {
                  setValue("cardNumber", "");
                }
              }}
            />
          ) : (
            <Stack
              spacing={2}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                width="100%"
                spacing={1}
              >
                <Warning sx={{ color: "warning.main" }} />
                <Typography variant="body1">
                  Você não possui nenhum plano de saúde cadastrado ou ativo.
                </Typography>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between "
                width="100%"
                spacing={1}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowAffiliationModal(true)}
                  startIcon={<Add />}
                >
                  Novo plano de saúde
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setTabValue(0)}
                  startIcon={<AttachMoney />}
                >
                  Atendimento particular
                </Button>
              </Stack>
            </Stack>
          )}

          {!loadingOrganizationAffiliation &&
            !loadingCoverage &&
            healthInsuranceWatch && (
              <>
                <FormMultipleAutoComplete
                  variant="filled"
                  name="procedures"
                  control={control}
                  label="Procedimentos"
                  required={true}
                  selectOptions={generateProcedureOptions()}
                  loading={loadingOrganizationAffiliation}
                />
                {!(loadingOrganizationAffiliation || loadingCoverage) && (
                  <FormTextInput
                    name="cardNumber"
                    defaultValue=""
                    control={control}
                    label="Número da carteirinha"
                    required={true}
                    disabled={loadingOrganizationAffiliation}
                  />
                )}
              </>
            )}
        </Stack>
      </CustomTabPanel>

      <FormButtonGroup
        nextButtonText={"Finalizar"}
        onGoBackButtonClick={() => onBackButtonClick()}
        onNextButtonClick={handleSubmit(onSubmit)}
        disabled={tabValue === 1 && !healthInsuranceWatch && !cardNumber}
        loading={loading}
      />

      {showReturnConfirmation && hasFluxPayExtension && !loading && (
        <ReturnAppointmentConfirmationDialog
          open={showReturnConfirmation && hasFluxPayExtension && !loading}
          onClose={() => setShowReturnConfirmation(false)}
          onConfirm={handleReturnConfirmation}
          appointmentDetails={returnAppointmentDetails}
        />
      )}

      {/* Modal de Afiliação */}
      <AffiliationModal
        open={showAffiliationModal}
        isEditing={false}
        onClose={() => {
          setShowAffiliationModal(false);
          // Atualiza a lista de planos ao fechar o modal
          if (organization?.id) {
            fetchOrganizationAffiliation({
              primaryOrganization: organization.id,
              count: 50,
            });
          }
        }}
        handleRefetch={async () => {
          // Atualiza a lista de planos após adicionar um novo plano
          if (organization?.id) {
            await fetchOrganizationAffiliation({
              primaryOrganization: organization.id,
              count: 50,
            });
            // Fecha o modal após atualizar a lista
            setShowAffiliationModal(false);
          }
        }}
      />
    </Stack>
  );
}
